'use client';

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { ResponsiveImageSource } from '@/app/components/media/app-image';
import { ResponsiveImage } from '@/app/components/media/app-image';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_FRAME_COUNT, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_MEDIA_QUERY } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import { SpartanProductExteriorThreesixtyEdgeGradients } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/edge-gradients';
import { SpartanProductExteriorThreesixtyMediaStage } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/media-stage';
import type { SpartanProductExteriorThreesixtyProps } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/types';
import { useThreesixtyLoader } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/useThreesixtyLoader';
import { getThreesixtySampledFrameIndexes, getThreesixtySampledPresetIndexes, getThreesixtyStageBackground } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/utils';
import { SPARTAN_PRODUCT_EXTERIOR_TRACKING } from '@/app/lcv/components/sections/spartan/products/exterior/tracking';
import styles from '@/app/lcv/components/sections/spartan/products/exterior-inline/threesixty/styles.module.css';
import { SpartanProductExteriorInlineViewer } from '@/app/lcv/components/sections/spartan/products/exterior-inline/threesixty/viewer';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { stripHtmlTags } from '@/app/utils/format';
const MOBILE_POSTER_WIDTHS = [640, 750, 828, 1080, 1280] as const;
const DESKTOP_POSTER_WIDTHS = [1280, 1920, 2560, 2880] as const;
interface InlineControlsStyle extends CSSProperties {
  '--inline-threesixty-controls-background'?: string;
}
export const SpartanProductExteriorInlineThreesixty = t0 => {
  const $ = _c(88);
  const {
    frames,
    presetIndexes,
    alt,
    background,
    isMobileCropped: t1,
    dragCursorTone: t2,
    dragGuideTone
  } = t0;
  const isMobileCropped = t1 === undefined ? false : t1;
  const dragCursorTone = t2 === undefined ? "default" : t2;
  const [hasEnteredPreloadRange, setHasEnteredPreloadRange] = useState(false);
  const [isMobileSequence, setIsMobileSequence] = useState(null);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const isMobileViewport = useMediaQuery(SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_MEDIA_QUERY, false) ?? false;
  let t3;
  if ($[0] !== frames.length) {
    t3 = getThreesixtySampledFrameIndexes(frames.length, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_FRAME_COUNT);
    $[0] = frames.length;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const mobileFrameIndexes = t3;
  const shouldUseMobileSequence = isMobileSequence ?? isMobileViewport;
  let t4;
  if ($[2] !== frames || $[3] !== mobileFrameIndexes || $[4] !== shouldUseMobileSequence) {
    t4 = shouldUseMobileSequence && frames.length > mobileFrameIndexes.length ? mobileFrameIndexes.map(index => frames[index]) : frames;
    $[2] = frames;
    $[3] = mobileFrameIndexes;
    $[4] = shouldUseMobileSequence;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const activeFrames = t4;
  let t5;
  if ($[6] !== frames.length || $[7] !== mobileFrameIndexes || $[8] !== presetIndexes || $[9] !== shouldUseMobileSequence) {
    t5 = shouldUseMobileSequence && presetIndexes && frames.length > mobileFrameIndexes.length ? getThreesixtySampledPresetIndexes(presetIndexes, mobileFrameIndexes, frames.length) : presetIndexes;
    $[6] = frames.length;
    $[7] = mobileFrameIndexes;
    $[8] = presetIndexes;
    $[9] = shouldUseMobileSequence;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const activePresetIndexes = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      rootMargin: "0px",
      threshold: 0,
      triggerOnce: false,
      initialInView: false
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    ref: setPreloadViewportRef,
    inView: isInPreloadRange
  } = useInView(t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      rootMargin: "50% 0px",
      threshold: 0,
      triggerOnce: false,
      initialInView: false
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    ref: setActiveViewportRef,
    inView: isNearViewport
  } = useInView(t7);
  const t8 = !isNearViewport;
  let t9;
  if ($[13] !== isNearViewport || $[14] !== t8) {
    t9 = {
      isInteractiveBoosted: isNearViewport,
      mode: "interactive",
      paused: t8,
      requireCompleteSequence: true
    };
    $[13] = isNearViewport;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const loader = useThreesixtyLoader(activeFrames, hasEnteredPreloadRange && isMobileSequence !== null, t9);
  let t10;
  let t11;
  if ($[16] !== isInPreloadRange || $[17] !== isMobileViewport) {
    t10 = () => {
      if (!isInPreloadRange) {
        return;
      }
      setIsMobileSequence(current => current ?? isMobileViewport);
      setHasEnteredPreloadRange(true);
    };
    t11 = [isInPreloadRange, isMobileViewport];
    $[16] = isInPreloadRange;
    $[17] = isMobileViewport;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setIsCanvasReady(false);
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== activeFrames) {
    t13 = [activeFrames];
    $[21] = activeFrames;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  useEffect(t12, t13);
  let t14;
  if ($[23] !== setActiveViewportRef || $[24] !== setPreloadViewportRef) {
    t14 = node => {
      setPreloadViewportRef(node);
      setActiveViewportRef(node);
    };
    $[23] = setActiveViewportRef;
    $[24] = setPreloadViewportRef;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const setTrackRef = t14;
  let t15;
  if ($[26] !== background) {
    t15 = getThreesixtyStageBackground(background);
    $[26] = background;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const stageBackground = t15;
  let t16;
  if ($[28] !== stageBackground) {
    t16 = stageBackground ? {
      background: stageBackground
    } : undefined;
    $[28] = stageBackground;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const stageStyle = t16;
  const controlsBackground = typeof background === "string" ? background : background?.bottom;
  let t17;
  if ($[30] !== controlsBackground) {
    t17 = controlsBackground ? {
      "--inline-threesixty-controls-background": controlsBackground
    } : undefined;
    $[30] = controlsBackground;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const controlsStyle = t17;
  const isInteractionEnabled = loader.isInteractionReady && loader.loadState === "ready";
  const isPosterVisible = !isInteractionEnabled || !isCanvasReady;
  const t18 = isMobileCropped ? "116vw" : "100vw";
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      f: "auto",
      q: 75
    };
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== activeFrames[0] || $[34] !== t18) {
    t20 = {
      src: activeFrames[0],
      media: "(max-width: 1023px)",
      sizes: t18,
      widths: MOBILE_POSTER_WIDTHS,
      params: t19
    };
    $[33] = activeFrames[0];
    $[34] = t18;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      f: "auto",
      q: 75
    };
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== activeFrames[0]) {
    t22 = {
      src: activeFrames[0],
      media: "(min-width: 1024px)",
      sizes: "100vw",
      widths: DESKTOP_POSTER_WIDTHS,
      params: t21
    };
    $[37] = activeFrames[0];
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t20 || $[40] !== t22) {
    t23 = [t20, t22];
    $[39] = t20;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const posterSources = t23;
  if (frames.length === 0) {
    return null;
  }
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = createDataAttributes({
      test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyTrack
    });
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = isMobileCropped ? styles.mobileCroppedStage : "size-full";
  let t26;
  if ($[43] !== t25) {
    t26 = cn("absolute top-0 left-0 overflow-clip", t25);
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = createDataAttributes({
      test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyStage
    });
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  const t28 = hasEnteredPreloadRange ? "eager" : "lazy";
  const t29 = isPosterVisible ? "opacity-100" : "opacity-0";
  let t30;
  if ($[46] !== t29) {
    t30 = cn("pointer-events-none absolute inset-0 z-10 size-full transition-opacity duration-300 motion-reduce:duration-100", t29);
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== posterSources || $[49] !== t28 || $[50] !== t30) {
    t31 = <ResponsiveImage sources={posterSources} alt="" width={960} height={768} objectFit="contain" fade={false} loading={t28} fetchPriority="low" aria-hidden="true" className={t30} testId={SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyPreviewPoster} />;
    $[48] = posterSources;
    $[49] = t28;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== alt) {
    t32 = stripHtmlTags(alt);
    $[52] = alt;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setIsCanvasReady(true);
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  let t34;
  if ($[55] !== activeFrames || $[56] !== activePresetIndexes || $[57] !== background || $[58] !== dragCursorTone || $[59] !== dragGuideTone || $[60] !== hasEnteredPreloadRange || $[61] !== isInteractionEnabled || $[62] !== loader || $[63] !== t32) {
    t34 = <SpartanProductExteriorInlineViewer frames={activeFrames} presetIndexes={activePresetIndexes} alt={t32} background={background} loader={loader} isPreloadEnabled={hasEnteredPreloadRange} isInteractionEnabled={isInteractionEnabled} dragCursorTone={dragCursorTone} dragGuideTone={dragGuideTone} onCanvasReady={t33} />;
    $[55] = activeFrames;
    $[56] = activePresetIndexes;
    $[57] = background;
    $[58] = dragCursorTone;
    $[59] = dragGuideTone;
    $[60] = hasEnteredPreloadRange;
    $[61] = isInteractionEnabled;
    $[62] = loader;
    $[63] = t32;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t31 || $[66] !== t34) {
    t35 = <SpartanProductExteriorThreesixtyMediaStage className="absolute inset-0 size-full">{t31}{t34}</SpartanProductExteriorThreesixtyMediaStage>;
    $[65] = t31;
    $[66] = t34;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== stageStyle || $[69] !== t26 || $[70] !== t27 || $[71] !== t35) {
    t36 = <div style={stageStyle} className={t26} {...t27}>{t35}</div>;
    $[68] = stageStyle;
    $[69] = t26;
    $[70] = t27;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  if ($[73] !== background) {
    t37 = <SpartanProductExteriorThreesixtyEdgeGradients background={background} />;
    $[73] = background;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t36 || $[76] !== t37) {
    t38 = <div className="aspect-5-4 relative w-full overflow-hidden lg:aspect-auto lg:h-[min(80vw,100svh)]">{t36}{t37}</div>;
    $[75] = t36;
    $[76] = t37;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  const t39 = Boolean(activePresetIndexes?.length);
  let t40;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = createDataAttributes({
      test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyControlsHost
    });
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] !== controlsStyle || $[80] !== t39 || $[81] !== t40) {
    t41 = <div className={styles.controlsHost} data-has-presets={t39} style={controlsStyle} {...t40} />;
    $[79] = controlsStyle;
    $[80] = t39;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== setTrackRef || $[84] !== t24 || $[85] !== t38 || $[86] !== t41) {
    t42 = <div ref={setTrackRef} className="relative" {...t24}>{t38}{t41}</div>;
    $[83] = setTrackRef;
    $[84] = t24;
    $[85] = t38;
    $[86] = t41;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  return t42;
};